<template>
  <div class="variant-manager">
    <div class="d-flex flex-wrap align-end mb-4 pb-3 border-bottom" style="gap: 8px">
      <h3 class="font-weight-bold white--text mb-1" :class="$vuetify.breakpoint.xsOnly ? 'text-h6' : 'text-h5'">Presentaciones</h3>
      <v-spacer />
      <v-btn
        v-if="!simplificado"
        color="primary"
        class="text-none font-weight-bold"
        depressed
        small
        @click="abrirDialogCrear"
      >
        <v-icon left size="18">mdi-plus</v-icon>
        Nueva Variante
      </v-btn>
    </div>

    <!-- Una sola presentación: código y SKU se editan desde "Información",
         acá solo queda la salida a agregar una variante si hace falta. -->
    <v-card v-if="simplificado" flat color="#1a1a1a" class="pa-4 variant-item">
      <div class="text-subtitle-2 white--text font-weight-bold mb-1">
        {{ (presentaciones[0] && presentaciones[0].nombre) || 'Unidad' }}
      </div>
      <div class="text-caption grey--text d-flex align-center flex-wrap mb-3">
        <v-icon x-small left color="grey darken-1">mdi-barcode</v-icon>
        <span class="mono-font">{{ (presentaciones[0] && presentaciones[0].codigo_barras) || '---' }}</span>
        <template v-if="presentaciones[0] && presentaciones[0].sku">
          <span class="mx-2 grey--text text--darken-2">•</span>
          <v-icon x-small left color="grey darken-1">mdi-pound</v-icon>
          <span class="mono-font">{{ presentaciones[0].sku }}</span>
        </template>
      </div>
      <div class="text-caption grey--text mb-4">
        El código de barras y el SKU se editan desde la pestaña "Información".
      </div>
      <v-btn color="primary" class="text-none font-weight-bold" depressed small @click="abrirDialogCrear">
        <v-icon left size="18">mdi-plus</v-icon>
        Agregar variante
      </v-btn>
    </v-card>

    <div v-else class="variants-list">
      <v-card
        v-for="pp in presentaciones"
        :key="pp.id"
        flat
        color="#1a1a1a"
        class="mb-2 variant-item transition-all"
        :class="{ 'is-editing': editandoId === pp.id }"
      >
        <div v-if="editandoId !== pp.id" class="d-flex flex-wrap align-center pa-3">

          <div class="flex-grow-1 min-w-200">
            <div class="d-flex align-center mb-1">
              <v-chip
                v-if="pp.es_base"
                x-small
                label
                color="amber darken-4"
                text-color="amber lighten-2"
                class="mr-2 font-weight-bold px-2"
              >
                BASE
              </v-chip>
              <v-chip
                v-else-if="tipoStock === 'BASE'"
                x-small
                label
                color="blue-grey darken-3"
                text-color="blue-grey lighten-3"
                class="mr-2 px-2"
              >
                DERIVADA
              </v-chip>
              <span class="text-subtitle-2 white--text font-weight-bold leading-tight">
                {{ pp.nombre || 'Sin nombre definido' }}
              </span>
            </div>

            <div class="text-caption grey--text d-flex align-center flex-wrap">
              <v-icon x-small left color="grey darken-1">mdi-barcode</v-icon>
              <span class="mono-font">{{ pp.codigo_barras || '---' }}</span>
              <template v-if="pp.sku">
                <span class="mx-2 grey--text text--darken-2">•</span>
                <v-icon x-small left color="grey darken-1">mdi-pound</v-icon>
                <span class="mono-font">{{ pp.sku }}</span>
              </template>
              <span class="mx-2 grey--text text--darken-2">•</span>
              <span :class="tipoStock === 'BASE' && !pp.es_base ? 'primary--text font-weight-bold' : ''">
                Factor: x{{ pp.factor_base }}
              </span>
            </div>
          </div>

          <div class="d-flex align-center gap-4 px-4 min-w-150 indicators-container">
            <div class="status-indicator" :class="{ 'text--white': pp.es_vendible, 'grey--text text--darken-2': !pp.es_vendible }">
              <v-icon x-small :color="pp.es_vendible ? 'success' : 'grey darken-3'" class="mr-1">mdi-circle</v-icon> Venta
            </div>
            <div class="status-indicator" :class="{ 'text--white': pp.es_comprable, 'grey--text text--darken-2': !pp.es_comprable }">
              <v-icon x-small :color="pp.es_comprable ? 'success' : 'grey darken-3'" class="mr-1">mdi-circle</v-icon> Compra
            </div>
            <div class="status-indicator" :class="{ 'text--white': pp.activo, 'grey--text text--darken-2': !pp.activo }">
              <v-icon x-small :color="pp.activo ? 'success' : 'grey darken-3'" class="mr-1">mdi-circle</v-icon> Activo
            </div>
          </div>

          <div class="d-flex align-center ml-2">
            <v-btn icon small class="edit-btn" @click="activarEdicion(pp)">
              <v-icon size="18" color="grey lighten-1">mdi-pencil-outline</v-icon>
            </v-btn>
          </div>
        </div>

        <div v-else class="pa-4 bg-editing">
          <div class="d-flex align-center mb-4">
            <span class="text-subtitle-2 primary--text font-weight-bold">Editando variante</span>
            <v-spacer />
            <v-btn small text class="text-none grey--text mr-2" @click="cancelarEdicion">Cancelar</v-btn>
            <v-btn small depressed color="primary" class="text-none font-weight-bold px-4" @click="submitForm" :loading="loadingSubmit">Guardar</v-btn>
          </div>

          <v-row dense class="mb-2">
            <v-col cols="12" sm="4">
              <span class="input-label">Nombre</span>
              <v-text-field v-model="form.nombre" dense filled dark hide-details class="custom-input mt-1" />
            </v-col>
            <v-col cols="12" sm="3">
              <span class="input-label">Código de Barras</span>
              <div class="d-flex align-center mt-1" style="gap: 4px">
                <v-text-field v-model="form.codigo_barras" dense filled dark hide-details prepend-inner-icon="mdi-barcode-scan" class="custom-input" />
                <v-btn
                  icon
                  small
                  :loading="loadingGenerar"
                  title="Generar código automáticamente"
                  @click="generarCodigo"
                >
                  <v-icon size="18" color="grey lighten-1">mdi-auto-fix</v-icon>
                </v-btn>
              </div>
            </v-col>
            <v-col cols="12" sm="2">
              <span class="input-label">SKU</span>
              <v-text-field v-model="form.sku" dense filled dark hide-details class="custom-input mt-1" />
            </v-col>
            <v-col cols="12" sm="3">
              <span class="input-label">Factor</span>
              <v-text-field v-model.number="form.factor_base" type="number" dense filled dark hide-details prefix="x" class="custom-input mt-1" />
            </v-col>
          </v-row>

          <div class="d-flex align-center flex-wrap gap-4 mt-4 pt-3 border-top-soft">
            <v-switch v-model="form.es_vendible" label="Venta" dense hide-details class="custom-switch mt-0" />
            <v-switch v-model="form.es_comprable" label="Compra" dense hide-details class="custom-switch mt-0" />
            <v-switch v-model="form.activo" label="Habilitado" dense hide-details class="custom-switch mt-0" />
            <v-switch v-model="form.es_base" label="Unidad Base" readonly dense hide-details class="custom-switch mt-0 opacity-50" />
          </div>
        </div>
      </v-card>
    </div>

    <v-dialog v-model="dialog" max-width="500" overlay-color="black" overlay-opacity="0.8" :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card color="#1a1a1a" class="creation-dialog">
        <v-card-title class="white--text text-h6 font-weight-bold pa-5 border-bottom-soft">
          Crear Presentación
        </v-card-title>
        <v-card-text class="pa-5">
          <div class="mb-4">
            <span class="input-label">Nombre</span>
            <v-text-field v-model="form.nombre" filled dark dense hide-details class="custom-input mt-1" />
          </div>
          <v-row dense>
            <v-col cols="12" sm="4">
              <span class="input-label">Factor</span>
              <v-text-field v-model.number="form.factor_base" type="number" filled dark dense hide-details prefix="x" class="custom-input mt-1" />
            </v-col>
            <v-col cols="12" sm="4">
              <span class="input-label">Código de barras</span>
              <v-text-field v-model="form.codigo_barras" filled dark dense hide-details class="custom-input mt-1" />
            </v-col>
            <v-col cols="12" sm="4">
              <span class="input-label">SKU</span>
              <v-text-field v-model="form.sku" filled dark dense hide-details class="custom-input mt-1" />
            </v-col>
          </v-row>
          <div class="mt-5 pa-4 rounded bg-surface d-flex flex-wrap justify-space-between align-center" style="gap: 12px 16px">
            <v-switch v-model="form.es_vendible" label="Venta" dense hide-details class="custom-switch mt-0" />
            <v-switch v-model="form.es_comprable" label="Compra" dense hide-details class="custom-switch mt-0" />
            <v-switch v-model="form.es_base" label="Base" dense hide-details class="custom-switch mt-0" />
          </div>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer />
          <v-btn text class="text-none grey--text" @click="cerrarDialog">Cancelar</v-btn>
          <v-btn color="primary" depressed class="text-none font-weight-bold px-6" @click="submitForm" :loading="loadingSubmit">Crear Variante</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  props: {
    productoId: {},
    value: {},
    descripcionBase: {},
    tipoStock: {},
    // Opt-in para ver todos los campos aunque haya una sola presentación
    // (el default de "simplificado" es automático por cantidad).
    modoAvanzado: { type: Boolean, default: false },
  },

  data() {
    return {
      presentaciones: [],
      editandoId: null,
      dialog: false,
      loadingSubmit: false,
      loadingGenerar: false,
      form: this.formVacio()
    };
  },

  computed: {
    // Con una sola presentación, factor/es_base/es_vendible/es_comprable/activo
    // casi nunca hace falta tocarlos: se esconden salvo que el usuario pida
    // "Modo avanzado" o el producto ya tenga más de una presentación.
    simplificado() {
      return !this.modoAvanzado && this.presentaciones.length <= 1;
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.presentaciones = (val || []).map(p => ({ ...p }));
      }
    }
  },

  methods: {
    formVacio() {
      return {
        nombre: "",
        factor_base: 1,
        codigo_barras: "",
        sku: "",
        es_base: false,
        es_vendible: true,
        es_comprable: false,
        activo: true
      };
    },

    activarEdicion(pp) {
      this.editandoId = pp.id;
      // Al separar la vista de la edición, clonar el objeto es mucho más limpio
      this.form = JSON.parse(JSON.stringify(pp));
    },

    cancelarEdicion() {
      this.editandoId = null;
    },

    abrirDialogCrear() {
      this.form = this.formVacio();
      this.dialog = true;
    },

    cerrarDialog() {
      this.dialog = false;
    },

    async submitForm() {
      if (!this.form.nombre?.trim()) return;

      this.loadingSubmit = true;

      try {
        const url = `/productos/productos/${this.productoId}/presentaciones` +
          (this.editandoId ? `/${this.editandoId}` : '');

        const method = this.editandoId ? 'patch' : 'post';

        await axios[method](url, this.form);

        this.$emit("updated");
        this.editandoId = null;
        this.cerrarDialog();
      } catch (error) {
        console.error("Error al guardar la presentación:", error);
        this.mostrarError(error);
      } finally {
        this.loadingSubmit = false;
      }
    },

    async generarCodigo() {
      if (!this.editandoId) return;

      this.loadingGenerar = true;
      try {
        const url = `/productos/productos/${this.productoId}/presentaciones/${this.editandoId}/generar-codigo`;
        const { data } = await axios.post(url);
        this.form.codigo_barras = data.codigo_barras;
      } catch (error) {
        console.error("Error al generar el código:", error);
        this.mostrarError(error);
      } finally {
        this.loadingGenerar = false;
      }
    },

    // DRF puede devolver {"detail": "..."} o {"campo": ["mensaje"]} (ValidationError
    // por campo, ej. codigo_barras/sku duplicados) — se muestra el primero que haya.
    mensajeDeError(error) {
      const data = error.response && error.response.data;
      if (!data) return "Ocurrió un error inesperado.";
      if (typeof data === "string") return data;
      if (data.detail) return data.detail;
      const primerCampo = Object.keys(data)[0];
      if (primerCampo) {
        const valor = data[primerCampo];
        return Array.isArray(valor) ? valor[0] : String(valor);
      }
      return "Ocurrió un error inesperado.";
    },

    mostrarError(error) {
      this.$updateBus.$emit("showSnackBar", {
        show: true,
        button: false,
        right: false,
        top: false,
        color: "error",
        text: this.mensajeDeError(error),
        icon: "mdi-alert",
        timeout: 4000,
      });
    }
  }
};
</script>

<style scoped lang="scss">
$border-color: rgba(255, 255, 255, 0.08);
$bg-color: #1a1a1a;
$input-bg: #242424;

.variant-manager {
  font-family: 'Inter', system-ui, sans-serif;
}

/* Layout y Tarjetas */
.variant-item {
  border: 1px solid $border-color !important;
  border-radius: 6px !important;
  
  &.is-editing {
    border-color: var(--v-primary-base, #3ea6ff) !important;
    box-shadow: 0 4px 12px rgba(0,0,0,0.2) !important;
  }
}

.bg-editing {
  background: rgba(62, 166, 255, 0.03);
}

.bg-surface {
  background: rgba(255, 255, 255, 0.03);
}

/* Utilidades estructurales */
.border-bottom { border-bottom: 1px solid rgba(255, 255, 255, 0.12); }
.border-bottom-soft { border-bottom: 1px solid $border-color; }
.border-top-soft { border-top: 1px solid $border-color; }
.gap-4 { gap: 16px; }
.min-w-200 { min-width: 200px; }
.min-w-150 { min-width: 150px; }
.opacity-50 { opacity: 0.5; }
.transition-all { transition: all 0.2s ease; }
.leading-tight { line-height: 1.2; }

/* Tipografía */
.input-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 700;
  color: #888;
}

.mono-font {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
}

/* UI Elements */
.status-indicator {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  display: flex;
  align-items: center;
}

.edit-btn {
  opacity: 0;
  transition: opacity 0.2s;
}

.variant-item:hover .edit-btn {
  opacity: 1;
}

/* Inputs y Switches refinados */
.custom-input {
  ::v-deep .v-input__slot {
    background: $input-bg !important;
    border-radius: 4px !important;
    min-height: 36px !important;
    border: 1px solid transparent;
    transition: border-color 0.2s;
    
    &:focus-within {
      border-color: var(--v-primary-base, #3ea6ff);
    }
  }
  ::v-deep input {
    font-size: 14px !important;
    padding: 4px 0 !important;
  }
  ::v-deep .v-text-field__prefix {
    font-size: 14px !important;
    color: #888;
  }
}

.custom-switch {
  ::v-deep .v-label {
    font-size: 12px !important;
    font-weight: 500;
  }
  ::v-deep .v-input--selection-controls__ripple {
    display: none;
  }
}

.creation-dialog {
  border: 1px solid $border-color;
}
</style>